import apiService from './api';
import { newUuid } from './evaluationQueue';
import type { ApiResponse } from '../types';
import type {
  StocktakesResponse, StocktakeDetail, StocktakeCountPayload, StocktakeCountRecord, StocktakeKind,
} from '../types/nurseryStocktake';

/**
 * Nursery stocktake: walk a farm and count the lots in the rows.
 *
 * Counting happens out in the rows, where signal comes and goes, so every
 * count is written to storage with its own `client_uuid` BEFORE it is sent.
 * The server is idempotent on that id, so a count sent twice is applied once,
 * and applies it as the difference from what the page showed, so a count that
 * arrives late does not undo a pick made meanwhile.
 */

const stocktakeService = {
  async list(): Promise<ApiResponse<StocktakesResponse>> {
    return apiService.get('/nursery/stocktakes');
  },
  async start(farmId: number, blockId: number | null, kind: StocktakeKind = 'stocktake'): Promise<ApiResponse<{ id: number; joined: boolean }>> {
    return apiService.post('/nursery/stocktakes', { farm_id: farmId, block_id: blockId, kind });
  },
  async get(id: number): Promise<ApiResponse<StocktakeDetail>> {
    return apiService.get(`/nursery/stocktakes/${id}`);
  },
  async count(id: number, payload: StocktakeCountPayload): Promise<ApiResponse<{ count: StocktakeCountRecord; replayed: boolean }>> {
    return apiService.post(`/nursery/stocktakes/${id}/counts`, payload);
  },
  async finish(id: number): Promise<ApiResponse<StocktakeDetail>> {
    return apiService.post(`/nursery/stocktakes/${id}/finish`, {});
  },
};

export default stocktakeService;

// ── The queue ──

const KEY = 'factree_stocktake_queue_v1';

export interface QueuedCount {
  stocktakeId: number;
  payload: StocktakeCountPayload;
  /** Set when the server refused it; kept so the counter can see why. */
  refused?: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();
// Counts recordCount is sending right now; a flush leaves them to it.
const inFlight = new Set<string>();

function read(): QueuedCount[] {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]') as QueuedCount[]; } catch { return []; }
}
function write(items: QueuedCount[]) {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* storage blocked: sent live only */ }
  listeners.forEach(l => l());
}

export function subscribeStocktakeQueue(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/** What is waiting for one stocktake, latest count per lot. */
export function queuedCounts(stocktakeId: number): Map<number, QueuedCount> {
  const out = new Map<number, QueuedCount>();
  for (const q of read()) if (q.stocktakeId === stocktakeId) out.set(q.payload.lot_id, q);
  return out;
}

export function dropQueuedCount(clientUuid: string): void {
  write(read().filter(q => q.payload.client_uuid !== clientUuid));
}

function isNetworkError(e: unknown): boolean {
  const err = e as { offline?: boolean; status?: number } | null;
  return !!err && (err.offline === true || err.status == null || err.status === 0);
}

function refusal(e: unknown): string {
  return (e as Error)?.message || 'Refused by the server';
}

/**
 * Record a count. Resolves with the server's record, or `null` when it is
 * queued for later. Throws only when the server refused it.
 */
export async function recordCount(
  stocktakeId: number,
  input: { lot_id: number; shown: number; counted: number; note?: string | null },
): Promise<StocktakeCountRecord | null> {
  const payload: StocktakeCountPayload = { ...input, client_uuid: newUuid(), counted_at: new Date().toISOString() };
  // A newer count of the same lot replaces any still waiting.
  write([...read().filter(q => !(q.stocktakeId === stocktakeId && q.payload.lot_id === input.lot_id)), { stocktakeId, payload }]);
  inFlight.add(payload.client_uuid);
  try {
    const res = await stocktakeService.count(stocktakeId, payload);
    if (!res.success) throw new Error(res.error?.message || 'Refused by the server');
    dropQueuedCount(payload.client_uuid);
    return res.data.count;
  } catch (e) {
    if (isNetworkError(e)) return null;
    dropQueuedCount(payload.client_uuid);
    throw e;
  } finally {
    inFlight.delete(payload.client_uuid);
  }
}

/** Send what is waiting. Refused counts are marked, not dropped, so the page can say why. */
export async function flushStocktakeQueue(): Promise<{ sent: number; offline: boolean }> {
  let sent = 0;
  for (const q of read()) {
    if (q.refused || inFlight.has(q.payload.client_uuid)) continue;
    try {
      const res = await stocktakeService.count(q.stocktakeId, q.payload);
      if (!res.success) throw new Error(res.error?.message || 'Refused by the server');
      dropQueuedCount(q.payload.client_uuid);
      sent++;
    } catch (e) {
      if (isNetworkError(e)) return { sent, offline: true };
      write(read().map(x => x.payload.client_uuid === q.payload.client_uuid ? { ...x, refused: refusal(e) } : x));
    }
  }
  return { sent, offline: false };
}
